import { useEffect, useRef, useState } from 'react';
import { clamp } from '@src/services/timeline';
import { THUMB_STATUS, TRACK_HEIGHT } from '@src/constants/timeline';
import createFifoCache from '@src/services/fifoCache';
import { createByteStore } from '@src/services/byteStore';
import { joinMediaFile } from '@src/services/mediaFile';
import { planChunks, stripSeconds, viewTiles } from '@src/services/chunkPlan';
import { rememberVideoMeta } from '@src/services/videoMetaStore';
import {
  adoptTimelinePrewarm,
  stopTimelinePrewarm,
} from '@src/services/timelinePrewarm';
import {
  canUseWebCodecs,
  decodeChunk,
  dropLocalSource,
  fetchRange,
  localSourceOf,
  normalizeCacheKey,
  openKeyframeDecoder,
  readCache,
  readSampleTable,
  writeCache,
} from '@src/services/mediaExtractor';

// Frames are rasterized at this height, then downscaled by the clip. Kept well
// above the ~48px track height (× device pixel ratio) so they stay crisp on
// high-DPI screens instead of being upscaled.
const THUMB_HEIGHT = 120;
const JPEG_QUALITY = 0.85;
const DEFAULT_ASPECT = 16 / 9;

// One frame every ~N seconds on the <video> fallback, capped so a long video
// doesn't extract too many — each frame there is one sequential seek, measured
// at 2603ms for 21 frames.
const SECONDS_PER_THUMB = 3;
const MIN_THUMBS = 3;
const MAX_SEEK_THUMBS = 60;

// The WebCodecs strip holds one frame a second, whatever the video's length.
// That is what a tile shows down to 1s of zoom, and it is affordable because the
// bytes are already being downloaded for the waveform: measured, decoding the
// whole file and encoding 799 thumbnails out of it is 9.5s of work under a
// download that takes about two minutes. 799 frames is roughly 3.5MB cached.
const STRIP_SECONDS = 1;

// How often the strip so far is written back. Often enough that a reload loses
// little, rarely enough that a growing payload is not rewritten per chunk.
const SAVE_INTERVAL_MS = 4000;

// A screenful is a couple of dozen tiles; this only bites if a viewport somehow
// asks for hundreds, and keeps the lead pass bounded whatever the zoom.
const MAX_TARGETS = 48;

// Keyframe reads are small, so more of them can be in flight than whole chunks.
const LEAD_PARALLEL = 6;

// A range that fails takes its tile's frame with it, and until these existed
// nothing ever asked for it again — the moment stayed missing unless the user
// happened to move the view. Bounded, and spaced out, so a range that is
// genuinely gone costs a few seconds rather than a request loop.
const LEAD_ATTEMPTS = 3;
const LEAD_RETRY_MS = 1500;
// A chunk decode is retried from bytes already held, so this costs no request.
// Usually it is a sample straddling into a chunk that has not landed yet.
const CHUNK_ATTEMPTS = 3;
const CHUNK_RETRY_MS = 800;

// How long the view has to hold still before the queue is re-aimed at it.
const VIEW_SETTLE_MS = 150;

// The lane's tile height, which with the video's shape gives the tile width and
// so the span of video one tile covers. Kept here because the loader has to know
// what the tiles want before the lane has rendered any.
const THUMB_TILE_PX = TRACK_HEIGHT;

// A seek to (nearly) the current position fires no 'seeked' event, so capture
// straight away instead of waiting for one.
const SEEK_EPSILON_SEC = 0.01;
// Frames are published in bursts rather than one at a time: 60 setState calls
// are 60 re-renders of the whole track.
const EMIT_INTERVAL_MS = 150;
// Safety net: a seek that never settles must not hang the extraction forever.
const SEEK_TIMEOUT_MS = 4000;

// A damaged stretch of video kills the decoder for good — the element reports
// MEDIA_ERR_DECODE and every later seek on it fails. A fresh element can still
// read the rest of the file, so restart on one and skip the frame that broke it.
// Capped so a thoroughly broken file can't spin through a restart per frame.
const MAX_DECODER_RESTARTS = 6;

const idle = (status) => ({
  status,
  thumbnails: [],
  times: [],
  aspect: DEFAULT_ASPECT,
  videoDuration: 0,
  total: 0,
});

// Reuse a finished extraction when the track remounts (e.g. collapsing then
// expanding the timeline). Only READY results land here — a failure may be
// transient and worth retrying.
const resultCache = createFifoCache(4);

// An offscreen element loaded far enough to seek + draw. Rejects if the source
// can't be read at all. Streams from the url: metadata lands early and seeks
// fetch only the ranges they need, so the first frames appear without waiting
// on a full download.
const loadVideo = (url) => {
  // A url uploaded from this machine plays from the file on disk instead:
  // every seek reads locally, where a network seek fetches its whole GOP.
  const local = localSourceOf(url);
  const src = local ? URL.createObjectURL(local) : url;
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    // Required — a cross-origin video without this taints the canvas and
    // toBlob throws SecurityError (verified: thumbnails vanish without it). The
    // CORS blocks Trung saw come from S3 caching a no-CORS copy of the same url;
    // the fix is to force a fresh CORS request, not to drop this (see below).
    // Harmless on a local blob url, which is same-origin by definition.
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    video.muted = true;
    // Whichever fires first settles this; the other is a no-op on a settled
    // promise, so neither listener has to unhook the other.
    video.addEventListener('loadedmetadata', () => resolve(video), {
      once: true,
    });
    video.addEventListener(
      'error',
      () => {
        // Rejected before anyone could disposeVideo it, so let go of the blob
        // here or nothing ever will — and forget the dead handle, so the
        // caller's retry streams from the url instead of failing again.
        if (local) {
          URL.revokeObjectURL(src);
          dropLocalSource(url);
        }
        reject(new Error('video load failed'));
      },
      { once: true },
    );
    video.src = src;
  });
};

const disposeVideo = (video) => {
  if (!video) return;
  const { src } = video;
  video.removeAttribute('src');
  video.load();
  // Blob urls hold their blob until revoked; http(s) urls ignore this.
  if (src.startsWith('blob:')) URL.revokeObjectURL(src);
};

// An object URL keeps its blob alive until revoked, so anything we hand out and
// then drop has to be handed back.
const revokeAll = (urls) => urls.forEach((url) => URL.revokeObjectURL(url));

/**
 * Run `run` at most once every `interval`, dropping the calls in between.
 *
 * Both things that happen per frame are far too cheap to do per frame: one is a
 * setState that re-renders the whole track, the other rewrites a payload that
 * grows to 3.5MB. `leading` is false for work that should not fire on the very
 * first call — the cache is worth writing once there is something in it.
 */
const everyMs = (interval, run, leading = true) => {
  let last = leading ? 0 : performance.now();
  return () => {
    const now = performance.now();
    if (now - last < interval) return;
    last = now;
    run();
  };
};

/**
 * Extract evenly-spaced frames from a video as blob object URLs. Runs once per
 * url, over whichever of two paths the browser and the source allow:
 *
 * - WebCodecs (services/mediaExtractor): fetch the keyframes through HTTP Range
 *   and decode them directly. No seeking, so it does not pay the cost that made
 *   the old path slow.
 * - offscreen <video> + <canvas>, seeking to each mark in turn. The fallback for
 *   browsers without VideoDecoder, servers that ignore Range, and codecs the
 *   decoder refuses. Frames are emitted progressively as each seek settles, so
 *   the strip fills in rather than appearing at the end.
 *
 * On the fallback a frame that can't be decoded is skipped (on a fresh element,
 * since the decoder stays broken) rather than ending the strip; if extraction
 * still stops early it publishes what it captured with `total` reduced to match.
 * FAILED is only for having nothing at all. `aspect` sizes the tiles;
 * `videoDuration` sizes the clip. A finished result is cached per url, so
 * remounting reuses it without extracting again.
 */
const useVideoThumbnails = (videoUrl, view) => {
  const [state, setState] = useState(
    () =>
      resultCache.get(normalizeCacheKey(videoUrl)) ||
      idle(THUMB_STATUS.LOADING),
  );
  // The chunk order follows this. A ref rather than an effect dependency: the
  // view changes on every scroll and zoom, and restarting the download each
  // time is the opposite of what re-ordering is for.
  const viewRef = useRef(view);
  const aimRef = useRef(null);

  useEffect(() => {
    viewRef.current = view;
    if (!aimRef.current) return undefined;
    // A drag emits dozens of values; aiming at an intermediate one is work
    // thrown away.
    const timer = setTimeout(() => aimRef.current(), VIEW_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [view.fromSec, view.toSec, view.pxPerSec]);

  useEffect(() => {
    // Everything belonging to THIS extraction. A per-run object rather than refs
    // shared across runs: the effect restarts whenever the presigned url is
    // refreshed, and a shared `cancelled` flag let the new run clear the old
    // one's. The abandoned run then carried on — writing its half-built strip
    // into resultCache under the same (signature-stripped) key, and releasing
    // the NEW run's download out from under it, which is what left a filmstrip
    // stuck half-drawn until the page was reloaded.
    const self = {
      cancelled: false,
      joined: null,
      aim: null,
      wake: () => {},
      unfollow: null,
    };
    // The prewarm this run adopted, when there is one — see start(). Shared
    // with runWebCodecs, which leaves the front-to-back decode to it.
    let adopted = null;
    // Resolved by the cleanup, so a run parked on the whole download can be let
    // go of. Built here rather than at the await, so cancelling before the run
    // gets that far still unparks it.
    const cancelSignal = new Promise((resolve) => {
      self.wake = resolve;
    });
    // A wait that cancelling cuts short, so a retry backoff cannot hold a
    // torn-down run alive.
    const pause = (ms) =>
      Promise.race([
        new Promise((resolve) => setTimeout(resolve, ms)),
        cancelSignal,
      ]);

    if (!videoUrl) {
      setState(idle(THUMB_STATUS.FAILED));
      return undefined;
    }

    // Presigned urls change per request, so key the cache on the object itself.
    const cacheKey = normalizeCacheKey(videoUrl);
    // Already extracted this video — show it at once.
    const cached = resultCache.get(cacheKey);
    setState(cached || idle(THUMB_STATUS.LOADING));
    // Returning here is only safe when that result covers the whole video. It
    // used to return unconditionally, which meant nothing set `aimRef`: with an
    // incomplete result, moving the view had no route to the loader and any
    // moment it was missing stayed a skeleton until the page was reloaded. A
    // complete one has nothing left to ask for at any zoom, so there is nothing
    // for the loader to be reached for.
    if (cached?.gridComplete) return undefined;

    // ---------------------------------------------------------------- strip --
    // The strip and everything built on it live out here rather than inside the
    // WebCodecs run, because the cache has to be able to publish a finished
    // filmstrip before a single request has been made.

    // Frames by the second they stand for.
    const bySecond = new Map();
    // What the strip is OF. Filled from the cache first and confirmed by the
    // sample table later, so the shape below can be published either way round.
    const meta = {
      aspect: DEFAULT_ASPECT,
      videoDuration: 0,
      gridSec: STRIP_SECONDS,
      keyTimes: [],
      gopSec: 0,
    };
    // Whether the cache already held the whole grid when this run started.
    // Decided once, not tracked live: tiles aim at the grid instead of at
    // keyframes when it is set, and having that flip mid-session would shift
    // every coarse tile onto a different frame while the user watched.
    let gridReady = false;

    // The moments held, in order — what everything downstream is built from: the
    // state the lane reads, the payload written back, and the answer to whether a
    // moment still has to be fetched.
    const secondsHeld = () => [...bySecond.keys()].sort((a, b) => a - b);
    // The moments the strip should hold, from the length it now knows about.
    const gridSeconds = () => stripSeconds(meta.videoDuration, meta.gridSec);
    const isGridComplete = () =>
      meta.videoDuration > 0 && gridSeconds().every((sec) => bySecond.has(sec));

    // One shape for the strip, whether it is being published from the cache,
    // mid-load or finished. Object URLs are minted on the way out and kept on
    // the frame, so republishing does not mint a second one for the same blob.
    const strip = (status) => {
      const seconds = secondsHeld();
      return {
        status,
        thumbnails: seconds.map((second) => {
          const frame = bySecond.get(second);
          if (!frame.url) frame.url = URL.createObjectURL(frame.blob);
          return frame.url;
        }),
        times: seconds,
        aspect: meta.aspect,
        videoDuration: meta.videoDuration,
        // stripSeconds' own count, without building the list to measure it.
        total:
          meta.videoDuration > 0
            ? Math.ceil(meta.videoDuration / meta.gridSec)
            : 0,
        keyTimes: meta.keyTimes,
        gopSec: meta.gopSec,
        gridSec: meta.gridSec,
        gridComplete: gridReady,
      };
    };
    const publish = (status) => setState(strip(status));
    const emit = everyMs(EMIT_INTERVAL_MS, () => publish(THUMB_STATUS.LOADING));

    // A frame arrives once; the first one to claim a moment keeps it.
    const keep = (second, blob) => {
      if (bySecond.has(second)) return;
      bySecond.set(second, { blob });
      emit();
    };

    // Written back as it goes rather than at the end, so a reload part way
    // through keeps what it had and asks only for the rest. `gridSec` and
    // `complete` are what let the next visit read this back and decide it needs
    // nothing else WITHOUT touching the network — older entries carry neither,
    // and are read with the one spacing that has ever been used.
    const save = () => {
      const seconds = secondsHeld();
      writeCache(videoUrl, 'strip', {
        aspect: meta.aspect,
        videoDuration: meta.videoDuration,
        gridSec: meta.gridSec,
        complete: isGridComplete(),
        times: seconds,
        blobs: seconds.map((second) => bySecond.get(second).blob),
      });
    };
    const saveOccasionally = everyMs(SAVE_INTERVAL_MS, save, false);

    // Read before anything touches the network. A video never changes, so a
    // strip written on an earlier visit is still exactly right — and reading it
    // first is the difference between a filmstrip that is simply THERE on reload
    // and one that shimmers through a lazy mp4box chunk plus three requests
    // (the size probe, the 64KB head, then moov) before the cache is so much as
    // looked at. The home page's poster frame and the waveform peaks already
    // read their cache this way round; the strip was the one that did not.
    //
    // Answers whether the cache covers the whole video, in which case there is
    // nothing left to fetch at any zoom.
    const loadCached = async () => {
      const saved = await readCache(videoUrl, 'strip');
      if (self.cancelled || !saved?.times?.length) return false;
      meta.aspect = saved.aspect || DEFAULT_ASPECT;
      meta.videoDuration = saved.videoDuration || 0;
      meta.gridSec = saved.gridSec || STRIP_SECONDS;
      saved.times.forEach((second, at) => {
        if (saved.blobs?.[at]) bySecond.set(second, { blob: saved.blobs[at] });
      });
      // `complete` is only a shortcut. An entry written before it existed still
      // carries the duration and the moments held, which is all the answer needs.
      gridReady = saved.complete === true || isGridComplete();
      publish(gridReady ? THUMB_STATUS.READY : THUMB_STATUS.LOADING);
      return gridReady;
    };

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const thumbnails = [];
    // Where each frame really sits in the video, so the strip can place it
    // there instead of assuming a uniform grid.
    const times = [];
    let video = null;
    let aspect = DEFAULT_ASPECT;
    let videoDuration = 0;

    // Seek `element` to `target` and capture once the seek settles. Captures
    // straight away when already there (no 'seeked' fires), and rejects on the
    // element's own error or a stuck seek so the caller can recover.
    const captureFrame = (element, target) =>
      new Promise((resolve, reject) => {
        const capture = () => {
          try {
            ctx.drawImage(element, 0, 0, canvas.width, canvas.height);
            // A blob URL instead of a data URL: base64 inflates the bytes by a
            // third AND parks all 60 frames as strings inside React state
            // (measured: 699KB for one video). An object URL is a short string;
            // the pixels stay outside the JS heap.
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('encode failed'));
                  return;
                }
                resolve(URL.createObjectURL(blob));
              },
              'image/jpeg',
              JPEG_QUALITY,
            );
          } catch (error) {
            reject(error);
          }
        };

        if (Math.abs(target - element.currentTime) < SEEK_EPSILON_SEC) {
          capture();
          return;
        }

        let timer = 0;
        // One controller unhooks both listeners, so neither has to know about
        // the other.
        const controller = new AbortController();
        const stop = () => {
          clearTimeout(timer);
          controller.abort();
        };
        const { signal } = controller;
        element.addEventListener(
          'seeked',
          () => {
            stop();
            capture();
          },
          { signal },
        );
        element.addEventListener(
          'error',
          () => {
            stop();
            reject(new Error('decode failed'));
          },
          { signal },
        );
        timer = setTimeout(() => {
          stop();
          reject(new Error('seek timeout'));
        }, SEEK_TIMEOUT_MS);

        // eslint-disable-next-line no-param-reassign
        element.currentTime = target;
      });

    const run = async () => {
      try {
        video = await loadVideo(videoUrl);
        if (self.cancelled) return;

        const { duration } = video;
        if (!Number.isFinite(duration) || !duration) return;

        aspect =
          (video.videoWidth || THUMB_HEIGHT) /
          (video.videoHeight || THUMB_HEIGHT);
        videoDuration = duration;
        // Remembered on this path too. The element has just reported the same
        // two numbers the sample table would have, and a browser that lands here
        // lands here every time — leaving it out would mean the panel kept
        // jumping to length on exactly the browsers that never get the fast path.
        rememberVideoMeta(videoUrl, { durationSec: duration, aspect });
        const count = clamp(
          Math.round(duration / SECONDS_PER_THUMB),
          MIN_THUMBS,
          MAX_SEEK_THUMBS,
        );
        canvas.width = Math.round(THUMB_HEIGHT * aspect);
        canvas.height = THUMB_HEIGHT;

        // Size the clip and show the full skeleton while frames extract.
        setState({
          status: THUMB_STATUS.LOADING,
          thumbnails: [],
          times: [],
          aspect,
          videoDuration,
          total: count,
        });

        const maxTarget = Math.max(duration - 0.05, 0);
        let restarts = 0;
        // Publishing every single frame means 60 renders of the whole track.
        // At this cadence the strip still reads as filling in. Its own, not the
        // strip's `publish` above: these frames sit at the marks the seeks
        // settled on rather than on a grid, so they are held in their own arrays.
        const publishSeeked = everyMs(EMIT_INTERVAL_MS, () =>
          setState({
            status: THUMB_STATUS.LOADING,
            thumbnails: [...thumbnails],
            times: [...times],
            aspect,
            videoDuration,
            total: count,
          }),
        );

        for (let i = 0; i < count; i += 1) {
          const target = clamp(((i + 0.5) / count) * duration, 0, maxTarget);
          try {
            // One seek at a time.
            // eslint-disable-next-line no-await-in-loop
            const objectUrl = await captureFrame(video, target);
            if (self.cancelled) return;
            thumbnails.push(objectUrl);
            times.push(target);
            // Reveal frames as they land (they go left-to-right).
            publishSeeked();
          } catch (error) {
            if (restarts >= MAX_DECODER_RESTARTS) break;
            restarts += 1;
            // The element is unusable once its decoder fails; swap in a fresh
            // one and move past the frame that broke it.
            disposeVideo(video);
            video = null;
            // eslint-disable-next-line no-await-in-loop
            video = await loadVideo(videoUrl);
            if (self.cancelled) return;
          }
        }
      } catch (error) {
        // Nothing more to capture — publish whatever we already have.
      }

      if (self.cancelled) return;
      disposeVideo(video);
      video = null;
      if (!thumbnails.length) {
        setState(idle(THUMB_STATUS.FAILED));
        return;
      }
      // `total` drops to what we actually captured, so the lane spreads its
      // tiles over those instead of shimmering forever on frames that will
      // never arrive.
      const result = {
        status: THUMB_STATUS.READY,
        thumbnails,
        times,
        aspect,
        videoDuration,
        total: thumbnails.length,
      };
      resultCache.set(cacheKey, result);
      setState(result);
    };

    // Fast path: the file fetched a chunk at a time, nearest what is on screen
    // first, each decoded the moment it lands. `run()` above is the safety net
    // for browsers without VideoDecoder and codecs the decoder will not take.
    const controller = new AbortController();

    const runWebCodecs = async () => {
      // The sample table comes from a small range read (64KB + moov, measured
      // 638KB) so every frame's position is known before a single chunk has been
      // asked for — which is what lets the chunks be ordered at all.
      const table = await readSampleTable(videoUrl, controller.signal);
      const { totalSize, keyframes, keyTimes } = table;
      if (self.cancelled || !totalSize) throw new Error('cancelled');

      const chunks = planChunks(
        keyframes,
        totalSize,
        table.track.timescale,
        table.offsetSec,
      );
      if (!chunks.length) throw new Error('no chunks');

      // What the cache could only guess at (or had wrong, for an entry written
      // before the duration was known) is now known for certain.
      meta.aspect = table.aspect;
      meta.videoDuration = table.videoDuration;
      meta.keyTimes = keyTimes;
      meta.gopSec = keyTimes.length
        ? table.videoDuration / keyTimes.length
        : STRIP_SECONDS;
      publish(THUMB_STATUS.LOADING);
      // Somewhere the timeline can read it during a render, which IndexedDB is
      // not: it lays the ruler out and picks the Fit zoom from this length, so
      // learning it a few frames late means the whole panel jumps. Noted as soon
      // as the table is read rather than with the frames — the strip takes a
      // download to fill and the length is already known.
      rememberVideoMeta(videoUrl, {
        durationSec: table.videoDuration,
        aspect: table.aspect,
      });

      // The strip's own moments. Taken once: `secondsIn` runs per chunk, and a
      // 90-minute video's grid is 5400 entries to rebuild each time.
      const wantSeconds = gridSeconds();
      // Everything already in hand from a previous visit: no chunk of this file
      // needs fetching for the strip's sake. The waveform will pull it if it
      // needs it, and deep zoom will pull the piece it lands on. Re-tested here
      // because the real duration may complete a grid the cache could not judge.
      const complete = isGridComplete();
      // The same call loadCached makes, now that the real duration is known. With
      // the grid whole the tiles aim at it, which leaves the lead pass below with
      // nothing to ask for.
      if (complete) gridReady = true;

      const targetsNow = () => {
        const { fromSec, toSec, pxPerSec } = viewRef.current;
        if (!(pxPerSec > 0)) return [];
        const tiles = viewTiles({
          fromSec,
          toSec,
          durationSec: meta.videoDuration,
          tileSpanSec: (THUMB_TILE_PX * meta.aspect) / pxPerSec,
          keyTimes,
          gridSec: meta.gridSec,
          gopSec: meta.gopSec,
          gridComplete: gridReady,
          max: MAX_TARGETS,
        });
        return [...new Set(tiles.map((tile) => tile.target))];
      };

      // Keyframes the visible tiles aim at, fetched on their own rather than
      // with the run behind them: a tenth of the bytes for a picture a coarse
      // tile cannot tell apart. This is what fills a Fit-zoom strip in seconds
      // rather than a download.
      const leadPass = async (store, samples) => {
        // Opened before the first fetch, so each keyframe is decoded and shown
        // the moment its own bytes land. Waiting for the set would leave the
        // strip blank for as long as the slowest of them takes.
        const decoder = await openKeyframeDecoder({
          table,
          keyframes: samples,
          height: THUMB_HEIGHT,
          onFrame: keep,
        });

        // Left to right, which is the order a filmstrip is read in.
        let at = 0;
        const worker = async () => {
          while (at < samples.length && !self.cancelled) {
            const sample = samples[at];
            at += 1;
            const end = sample.offset + sample.size;
            try {
              if (!store.has(sample.offset, end)) {
                // eslint-disable-next-line no-await-in-loop
                const buffer = await fetchRange(
                  videoUrl,
                  sample.offset,
                  end - 1,
                  controller.signal,
                );
                if (self.cancelled) return;
                store.add(sample.offset, new Uint8Array(buffer));
              }
              // eslint-disable-next-line no-await-in-loop
              await decoder.ready();
              if (self.cancelled) return;
              decoder.push(sample, store.slice(sample.offset, end));
            } catch (error) {
              // One keyframe that will not arrive is one tile left on a
              // skeleton. Letting it reject out of here took the whole pass with
              // it: Promise.all below rejected, `finish()` — the only place the
              // decoder is ever closed — was skipped, and the strip fell all the
              // way through to the <video> path. A range that fails is now just
              // that range.
            }
          }
        };
        await Promise.all(
          Array.from({ length: LEAD_PARALLEL }, () => worker()),
        );
        // Always reached, so the decoder is always let go of — leaving it open
        // was the leak this used to have. But HOW it is let go of matters.
        //
        // A cancelled pass is dropped, not drained. `finish()` flushes first,
        // which makes the decoder emit every frame it still holds — each one
        // drawn into a canvas and read back — and on the cancel path that runs
        // while the component is being torn down and the download aborted in the
        // same tick. Nobody wants those frames. Draining for them was a burst of
        // media and GL work at the worst possible moment, and it is the one new
        // thing this path started doing when the leak was fixed.
        if (self.cancelled) {
          decoder.discard();
          return;
        }
        await decoder.finish();
        publish(THUMB_STATUS.LOADING);
      };

      /**
       * The keyframes the visible tiles are waiting for, retried until they are
       * all here or the attempts run out.
       *
       * The pass above swallows a range that fails, so without this a moment lost
       * to one bad request was lost for the rest of the session: nothing compared
       * what arrived against what was asked for, and the only thing that ever
       * called this again was the user moving the view. Sitting still left the
       * tile shimmering for good.
       */
      const leadKeyframes = async (store, targets) => {
        // Only moments the file actually HAS a keyframe for. A grid second has
        // none — that is what the chunk fill is for — and retrying one would
        // spin through every attempt for nothing.
        const wantedFrames = targets
          .map((second) => ({
            second,
            sample: keyframes[keyTimes.indexOf(second)],
          }))
          .filter((entry) => entry.sample);
        if (!wantedFrames.length) return;

        for (let attempt = 0; attempt < LEAD_ATTEMPTS; attempt += 1) {
          if (self.cancelled) return;
          // Recomputed each round: the chunk fill may have landed some of these
          // in the meantime, and asking twice is a request and a decode for a
          // picture already on screen.
          const missing = wantedFrames.filter(
            ({ second }) => !bySecond.has(second),
          );
          if (!missing.length) return;
          if (attempt > 0) {
            // eslint-disable-next-line no-await-in-loop
            await pause(LEAD_RETRY_MS * attempt);
            if (self.cancelled) return;
          }
          // eslint-disable-next-line no-await-in-loop
          await leadPass(
            store,
            missing.map((entry) => entry.sample),
          );
        }
      };

      const secondsIn = (chunk) =>
        wantSeconds.filter(
          (second) =>
            second >= chunk.fromSec &&
            second < chunk.toSec &&
            !bySecond.has(second),
        );

      // eslint-disable-next-line prefer-const
      let joined;
      const onChunk = async (index, chunk) => {
        // Retried from the store, which already holds the bytes — this costs no
        // request. It has to be retried SOMEWHERE: the chunk is marked held
        // before its listeners run, so mediaFile will never offer it again, and a
        // decode that failed once used to leave those seconds missing for the
        // rest of the session. A straddling sample whose neighbour has not landed
        // yet is the common case, and a moment later it has.
        for (let attempt = 0; attempt < CHUNK_ATTEMPTS; attempt += 1) {
          if (self.cancelled) return;
          const seconds = secondsIn(chunk);
          if (!seconds.length) break;
          try {
            // eslint-disable-next-line no-await-in-loop
            await decodeChunk({
              table,
              store: joined.store,
              chunk,
              seconds,
              height: THUMB_HEIGHT,
              signal: controller.signal,
              onFrame: keep,
            });
            // Decoded cleanly. Whatever it gave is all this chunk has to give —
            // a second can resolve to a sample that lives in the chunk before,
            // and going round again would never find it.
            break;
          } catch (error) {
            if (attempt + 1 >= CHUNK_ATTEMPTS) break;
            // eslint-disable-next-line no-await-in-loop
            await pause(CHUNK_RETRY_MS * (attempt + 1));
          }
        }
        saveOccasionally();
      };

      if (complete) {
        // Nothing of the file has to be fetched: with the grid whole, that is
        // what every tile aims at. The lead pass still runs, from a scratch store
        // of its own rather than by joining a download nothing else is asking
        // for, so a view somehow wanting a keyframe is still served.
        const scratch = createByteStore();
        const leadOnly = () => leadKeyframes(scratch, targetsNow());
        self.aim = leadOnly;
        aimRef.current = leadOnly;
        await leadOnly();
        if (self.cancelled) throw new Error('cancelled');
        save();
        return strip(THUMB_STATUS.READY);
      }

      if (adopted) {
        // The adopted prewarm owns the front-to-back decode, reading the file
        // on disk one chunk at a time — so nothing here joins the download,
        // which is what used to pull the whole file into memory for a video
        // that never left this machine. This run serves only the visible
        // tiles, same as the complete branch, and streams the rest in through
        // the adoption's onFrame.
        const scratch = createByteStore();
        const leadOnly = () => leadKeyframes(scratch, targetsNow());
        self.aim = leadOnly;
        aimRef.current = leadOnly;
        await leadOnly();
        if (self.cancelled) throw new Error('cancelled');
        await Promise.race([adopted.done, cancelSignal]);
        if (self.cancelled) throw new Error('cancelled');
        if (isGridComplete()) {
          save();
          return strip(THUMB_STATUS.READY);
        }
        // The prewarm ended with holes (samples its decoder refused, a file
        // handle that died under it). The chunk fill below retries those from
        // bytes of its own, exactly as if the cache had come back partial.
      }

      joined = joinMediaFile(videoUrl, { chunks, totalSize, onChunk });
      self.joined = joined;
      // A chunk whose frames are all in hand is still needed for the waveform,
      // but it goes to the back of the queue behind everything that would change
      // what is on screen.
      chunks.forEach((chunk, index) => {
        if (!secondsIn(chunk).length) joined.deferChunk(index);
      });
      // Everything the current view needs, and re-run whenever it changes. The
      // timeline auto-fits once it has measured itself, so the view at mount is
      // almost never the one the user ends up looking at — a one-shot lead pass
      // aims at moments nobody is waiting for any more, and the chunk fill only
      // ever produces grid seconds, so tiles aiming at a keyframe would sit on a
      // skeleton for good.
      let filling = false;
      let again = false;
      const fill = async () => {
        if (filling) {
          again = true;
          return;
        }
        filling = true;
        do {
          again = false;
          const targets = targetsNow();
          joined.setView(
            viewRef.current.fromSec,
            viewRef.current.toSec,
            targets,
          );
          joined.start();
          // eslint-disable-next-line no-await-in-loop
          await leadKeyframes(joined.store, targets);
        } while (again && !self.cancelled);
        filling = false;
      };
      self.aim = fill;
      aimRef.current = fill;
      await fill();

      // Raced against this run being cancelled. `done` settles only once the
      // WHOLE file has arrived, so awaiting it bare parked an abandoned run here
      // for as long as the download took — long enough for the next mount to
      // start its own, which this one then finished on top of.
      await Promise.race([joined.done.catch(() => {}), cancelSignal]);
      if (self.cancelled) throw new Error('cancelled');
      if (!bySecond.size) throw new Error('no frames decoded');
      save();
      return strip(THUMB_STATUS.READY);
    };

    const start = async () => {
      // A prewarm may still be decoding this video from its just-uploaded
      // local file. Adopt it rather than stopping it: it reads the file on
      // disk one chunk at a time — nothing here extracts cheaper — and it runs
      // to completion even if this track unmounts, which is what guarantees
      // the FIRST upload ends with a complete cached strip instead of the next
      // visit pulling the whole file back down from S3. This run seeds its
      // strip from the frames in hand and streams the rest as they decode.
      adopted = adoptTimelinePrewarm(videoUrl);
      if (adopted) {
        try {
          const shape = await adopted.ready;
          if (self.cancelled) return;
          meta.aspect = shape.aspect;
          meta.videoDuration = shape.videoDuration;
          meta.gridSec = shape.gridSec;
          // Subscribed BEFORE the frames Map is read, so a frame landing in
          // between is seen by one or both — `keep` drops the duplicate.
          self.unfollow = adopted.onFrame(keep);
          adopted.frames.forEach((blob, second) => {
            if (!bySecond.has(second)) bySecond.set(second, { blob });
          });
          publish(THUMB_STATUS.LOADING);
        } catch (error) {
          // Died before its sample table — nothing to adopt after all.
          adopted = null;
        }
      }
      if (!adopted) {
        // No job worth adopting: this run owns the extraction alone, so make
        // sure a dead prewarm's last flush is in the cache it reads next.
        await stopTimelinePrewarm(videoUrl);
      }
      if (self.cancelled) return;
      // A cached strip that covers the whole video answers every tile at every
      // zoom, so the sample table, the download and the decoder are all skipped
      // — the filmstrip is on screen before anything would have been requested.
      if (await loadCached()) {
        if (self.cancelled) return;
        resultCache.set(cacheKey, strip(THUMB_STATUS.READY));
        return;
      }
      if (canUseWebCodecs()) {
        try {
          const result = await runWebCodecs();
          if (self.cancelled) {
            revokeAll(result.thumbnails);
            return;
          }
          resultCache.set(cacheKey, result);
          setState(result);
          return;
        } catch (error) {
          if (controller.signal.aborted || self.cancelled) return;
        } finally {
          if (self.joined) self.joined.release();
          self.joined = null;
        }
      }
      await run();
    };

    start();

    return () => {
      self.cancelled = true;
      // Let go of the adopted prewarm's frames — the job itself keeps running,
      // because finishing is how the strip gets cached for the next visit.
      if (self.unfollow) self.unfollow();
      // Unpark a run waiting on the whole download, so it unwinds now and hands
      // the file back instead of finishing minutes later on a dead component.
      self.wake();
      controller.abort();
      disposeVideo(video);
      // Handed back here as well as in `start`'s finally, because the run may
      // still be unwinding and a join nobody released keeps the download alive
      // for the rest of the session. `release` ignores a second call.
      if (self.joined) self.joined.release();
      // Only ours to clear: a later run may already own the aim, and dropping
      // that would leave its filmstrip with no way to follow the view.
      if (aimRef.current === self.aim) aimRef.current = null;
      // A finished extraction lives on in resultCache and its urls stay valid
      // for the next mount. An abandoned one has no owner, so its frames would
      // leak for the lifetime of the document.
      if (!resultCache.get(cacheKey)) revokeAll(thumbnails);
    };
  }, [videoUrl]);

  return state;
};

export default useVideoThumbnails;
